import React, { useState } from 'react';
import {
  Play,
  MessageSquare,
  Bot,
  User,
  CheckCircle,
  Clock,
  Zap,
  ChevronRight,
  RefreshCw,
  BarChart3,
  Sparkles
} from 'lucide-react';

export type Theme = 'dark' | 'light';

export interface ChatAgentSimulationProps {
  theme?: Theme;
  agentId?: string;
}

interface Scenario {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'running' | 'passed' | 'failed';
  durationMs?: number;
  turns: { role: 'user' | 'assistant'; text: string }[];
}

const THEME_CONFIG = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    input: 'bg-neutral-700 border-neutral-600 text-white',
    hover: 'hover:bg-neutral-700'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    input: 'bg-white border-gray-300 text-gray-900',
    hover: 'hover:bg-gray-100'
  }
};

const MOCK_SCENARIOS: Scenario[] = [
  {
    id: 'greeting',
    name: 'Greeting flow',
    description: 'User says hello and receives a welcome response',
    icon: <MessageSquare className="w-4 h-4" />,
    status: 'passed',
    durationMs: 420,
    turns: [
      { role: 'user', text: 'Hi there!' },
      { role: 'assistant', text: 'Hello! How can I help you today?' }
    ]
  },
  {
    id: 'faq',
    name: 'FAQ lookup',
    description: 'User asks a common question and gets a knowledge-based answer',
    icon: <Zap className="w-4 h-4" />,
    status: 'passed',
    durationMs: 890,
    turns: [
      { role: 'user', text: 'What are your opening hours?' },
      { role: 'assistant', text: 'We are open Monday–Friday 9am–6pm and Saturday 10am–4pm. Sundays we are closed.' }
    ]
  },
  {
    id: 'complaint',
    name: 'Complaint handling',
    description: 'User reports an issue and is guided to resolution',
    icon: <Bot className="w-4 h-4" />,
    status: 'passed',
    durationMs: 1200,
    turns: [
      { role: 'user', text: 'My order hasn’t arrived yet.' },
      { role: 'assistant', text: 'I’m sorry to hear that. Could you share your order number?' },
      { role: 'user', text: 'It’s #7842.' },
      { role: 'assistant', text: 'I’ve looked up order #7842. It’s scheduled for delivery tomorrow. I can send you the tracking link if you’d like.' }
    ]
  },
  {
    id: 'multiturn',
    name: 'Multi-turn clarification',
    description: 'Agent asks follow-up questions before answering',
    icon: <Sparkles className="w-4 h-4" />,
    status: 'pending',
    turns: [
      { role: 'user', text: 'I need help with my account.' },
      { role: 'assistant', text: 'I can help with that. Is this about billing, security, or something else?' }
    ]
  }
];

const ChatAgentSimulation: React.FC<ChatAgentSimulationProps> = ({
  theme = 'dark',
  agentId
}) => {
  const colors = THEME_CONFIG[theme];
  const [scenarios, setScenarios] = useState<Scenario[]>(MOCK_SCENARIOS);
  const [expandedId, setExpandedId] = useState<string | null>('greeting');
  const [runningId, setRunningId] = useState<string | null>(null);

  const runScenario = (id: string) => {
    setRunningId(id);
    setScenarios(prev =>
      prev.map(s => (s.id === id ? { ...s, status: 'running' as const } : s))
    );
    setTimeout(() => {
      setScenarios(prev =>
        prev.map(s =>
          s.id === id
            ? { ...s, status: 'passed' as const, durationMs: 600 + Math.floor(Math.random() * 800) }
            : s
        )
      );
      setRunningId(null);
    }, 1500);
  };

  const runAll = () => {
    const pending = scenarios.filter(s => s.status === 'pending');
    if (pending.length === 0) return;
    setRunningId('all');
    let delay = 0;
    pending.forEach(s => {
      setTimeout(() => runScenario(s.id), delay);
      delay += 1600;
    });
    setTimeout(() => setRunningId(null), delay + 500);
  };

  const passedCount = scenarios.filter(s => s.status === 'passed').length;
  const avgDuration =
    scenarios.filter(s => s.durationMs).length > 0
      ? Math.round(
          scenarios.filter(s => s.durationMs).reduce((a, s) => a + (s.durationMs ?? 0), 0) /
            scenarios.filter(s => s.durationMs).length
        )
      : 0;

  return (
    <div className={`h-full ${colors.background} flex flex-col overflow-hidden`}>
      {/* Header */}
      <div className={`flex-shrink-0 px-6 py-4 border-b ${colors.border} ${colors.primary}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-lg font-semibold ${colors.text.primary} flex items-center gap-2`}>
              <Play className="w-5 h-5 text-indigo-400" />
              Agent Simulation
            </h1>
            <p className={`text-sm ${colors.text.muted} mt-0.5`}>
              Run conversation scenarios with mock data to validate agent behavior
            </p>
          </div>
          <button
            type="button"
            onClick={runAll}
            disabled={runningId !== null || scenarios.every(s => s.status === 'passed')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${colors.accent} text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
          >
            {runningId === 'all' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Run all pending
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`${colors.surface} rounded-xl border ${colors.border} p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-indigo-400" />
                <span className={`text-xs font-medium ${colors.text.muted}`}>Scenarios</span>
              </div>
              <p className={`text-2xl font-semibold ${colors.text.primary}`}>{scenarios.length}</p>
            </div>
            <div className={`${colors.surface} rounded-xl border ${colors.border} p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className={`text-xs font-medium ${colors.text.muted}`}>Passed</span>
              </div>
              <p className={`text-2xl font-semibold text-green-500`}>{passedCount}</p>
            </div>
            <div className={`${colors.surface} rounded-xl border ${colors.border} p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className={`text-xs font-medium ${colors.text.muted}`}>Avg. response</span>
              </div>
              <p className={`text-2xl font-semibold ${colors.text.primary}`}>{avgDuration}ms</p>
            </div>
            <div className={`${colors.surface} rounded-xl border ${colors.border} p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-indigo-400" />
                <span className={`text-xs font-medium ${colors.text.muted}`}>Success rate</span>
              </div>
              <p className={`text-2xl font-semibold ${colors.text.primary}`}>
                {scenarios.length ? Math.round((passedCount / scenarios.length) * 100) : 0}%
              </p>
            </div>
          </div>

          {/* Scenario cards */}
          <div className="space-y-3">
            <h2 className={`text-sm font-semibold ${colors.text.primary}`}>Simulation scenarios</h2>
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className={`${colors.surface} border ${colors.border} rounded-xl overflow-hidden`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === scenario.id ? null : scenario.id)}
                  className={`w-full flex items-center gap-4 p-4 text-left ${colors.hover} transition-colors`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      scenario.status === 'passed'
                        ? 'bg-green-500/20 text-green-500'
                        : scenario.status === 'running'
                        ? 'bg-amber-500/20 text-amber-500'
                        : 'bg-neutral-700 text-neutral-400'
                    }`}
                  >
                    {scenario.status === 'running' ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      scenario.icon
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${colors.text.primary}`}>{scenario.name}</p>
                    <p className={`text-xs ${colors.text.muted} truncate`}>{scenario.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {scenario.durationMs != null && (
                      <span className={`text-xs ${colors.text.muted}`}>{scenario.durationMs}ms</span>
                    )}
                    {scenario.status === 'pending' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          runScenario(scenario.id);
                        }}
                        disabled={runningId !== null}
                        className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${colors.accent} text-white hover:bg-indigo-700 disabled:opacity-50`}
                      >
                        <Play className="w-3 h-3" />
                        Run
                      </button>
                    )}
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${colors.text.muted} ${
                        expandedId === scenario.id ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                </button>

                {expandedId === scenario.id && (
                  <div className={`border-t ${colors.border} p-4 ${colors.secondary}`}>
                    <p className={`text-xs font-medium ${colors.text.muted} mb-3`}>Mock conversation</p>
                    <div className="space-y-2">
                      {scenario.turns.map((turn, i) => (
                        <div
                          key={i}
                          className={`flex gap-3 ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          {turn.role === 'assistant' && (
                            <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                              <Bot className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                          <div
                            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                              turn.role === 'user'
                                ? 'bg-indigo-600 text-white'
                                : `${colors.surface} border ${colors.border} ${colors.text.primary}`
                            }`}
                          >
                            {turn.text}
                          </div>
                          {turn.role === 'user' && (
                            <div className="w-7 h-7 rounded-full bg-neutral-600 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-neutral-300" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className={`text-xs ${colors.text.muted} text-center`}>
            Simulation uses mock data. Connect your agent API to run real scenarios.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatAgentSimulation;
